import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const ConfiguracionSecuencia = () => {
    const [config, setConfig] = useState({
        current_number: 1,
        initial_number: 1,
        auto_reset_daily: true,
        last_reset_date: null,
        updated_at: null
    });
    const [nextNumberInput, setNextNumberInput] = useState('1');
    const [initialNumberInput, setInitialNumberInput] = useState('1');
    const [autoResetDaily, setAutoResetDaily] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const fetchConfig = async () => {
        try {
            setLoading(true);
            setErrorMessage('');
            const res = await api.get('/api/orders/orders/sequence_config/', {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            if (res.data) {
                setConfig(res.data);
                setNextNumberInput(String(res.data.current_number ?? 1));
                setInitialNumberInput(String(res.data.initial_number ?? 1));
                setAutoResetDaily(res.data.auto_reset_daily !== false);
            }
        } catch (err) {
            console.error('Error fetching sequence config:', err);
            setErrorMessage('No se pudo cargar la configuración de secuencia.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConfig();
    }, []);

    const handleSaveSettings = async (e) => {
        if (e) e.preventDefault();
        const num = parseInt(nextNumberInput, 10);
        const initNum = parseInt(initialNumberInput, 10);

        if (isNaN(num) || num < 1) {
            setErrorMessage('El siguiente número debe ser mayor o igual a 1.');
            return;
        }
        if (isNaN(initNum) || initNum < 1) {
            setErrorMessage('El número inicial debe ser mayor o igual a 1.');
            return;
        }

        setSaving(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const res = await api.post(
                '/api/orders/orders/sequence_config/',
                {
                    current_number: num,
                    initial_number: initNum,
                    auto_reset_daily: autoResetDaily
                },
                { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE }
            );

            setConfig(res.data);
            setSuccessMessage(`Configuración guardada exitosamente. Próxima orden: #${res.data.current_number}`);
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (err) {
            console.error('Error saving sequence config:', err);
            setErrorMessage('Error al guardar la configuración.');
        } finally {
            setSaving(false);
        }
    };

    const handleResetNow = async () => {
        const initNum = parseInt(initialNumberInput, 10) || 1;
        const confirmReset = window.confirm(
            `¿Estás seguro de reiniciar el número de orden a #${initNum} en este momento?`
        );
        if (!confirmReset) return;

        setSaving(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const res = await api.post(
                '/api/orders/orders/sequence_config/',
                {
                    reset_now: true,
                    initial_number: initNum,
                    auto_reset_daily: autoResetDaily
                },
                { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE }
            );

            setConfig(res.data);
            setNextNumberInput(String(res.data.current_number));
            setSuccessMessage(`¡Secuencia reiniciada a #${res.data.current_number} exitosamente!`);
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (err) {
            console.error('Error resetting sequence config:', err);
            setErrorMessage('Error al reiniciar la secuencia de órdenes.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', padding: '24px' }}>
            <div style={{ maxWidth: '700px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <div style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '12px',
                            backgroundColor: '#dcfce7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '22px',
                            color: '#166534',
                            boxShadow: '0 2px 4px rgba(22, 101, 52, 0.1)'
                        }}>
                            <i className="bi bi-123"></i>
                        </div>
                        <div>
                            <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                                Turno y Número de Orden Diario
                            </h1>
                            <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
                                Configura el correlativo que se imprime en comandas y notas de entrega
                            </p>
                        </div>
                    </div>
                </div>

                {/* Main Card */}
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '16px',
                    padding: '24px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                    border: '1px solid #e2e8f0',
                    marginBottom: '20px'
                }}>
                    {/* Status Badge */}
                    <div style={{
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                    }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Próximo Número a Imprimir
                        </span>
                        <div style={{
                            fontSize: '52px',
                            fontWeight: '900',
                            color: '#15803d',
                            margin: '4px 0 8px 0',
                            letterSpacing: '-1px'
                        }}>
                            #{loading ? '...' : config.current_number}
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', fontSize: '12px' }}>
                            <span style={{
                                backgroundColor: config.auto_reset_daily ? '#dcfce7' : '#fee2e2',
                                color: config.auto_reset_daily ? '#166534' : '#991b1b',
                                padding: '4px 12px',
                                borderRadius: '999px',
                                fontWeight: '700'
                            }}>
                                {config.auto_reset_daily ? 'Auto-reinicio Diario a las 00:00 (Activo)' : 'Auto-reinicio Desactivado'}
                            </span>
                            {config.last_reset_date && (
                                <span style={{ backgroundColor: '#f1f5f9', color: '#475569', padding: '4px 12px', borderRadius: '999px' }}>
                                    Último reinicio: {config.last_reset_date}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Messages */}
                    {successMessage && (
                        <div style={{
                            backgroundColor: '#f0fdf4',
                            border: '1px solid #86efac',
                            color: '#166534',
                            padding: '12px 16px',
                            borderRadius: '10px',
                            fontSize: '14px',
                            fontWeight: '600',
                            marginBottom: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            {successMessage}
                        </div>
                    )}

                    {errorMessage && (
                        <div style={{
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fca5a5',
                            color: '#991b1b',
                            padding: '12px 16px',
                            borderRadius: '10px',
                            fontSize: '14px',
                            fontWeight: '600',
                            marginBottom: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            {errorMessage}
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSaveSettings}>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', fontSize: '14px', fontWeight: '700', color: '#334155', marginBottom: '8px' }}>
                                Establecer Siguiente Número de Orden:
                            </label>
                            <div style={{ position: 'relative', maxWidth: '300px' }}>
                                <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontWeight: '800', fontSize: '18px' }}>#</span>
                                <input
                                    type="number"
                                    min="1"
                                    value={nextNumberInput}
                                    onChange={(e) => setNextNumberInput(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px 14px 12px 34px',
                                        fontSize: '18px',
                                        fontWeight: '700',
                                        border: '2px solid #cbd5e1',
                                        borderRadius: '10px',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        color: '#0f172a'
                                    }}
                                    required
                                />
                            </div>
                            <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                                La siguiente comanda o venta emitida se registrará con este número.
                            </span>
                        </div>

                        {/* Auto-reset Configuration */}
                        <div style={{
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '16px',
                            marginBottom: '24px'
                        }}>
                            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: '8px' }}>
                                <div>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>
                                        Reiniciar automáticamente a las 00:00 (12:00 AM)
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                        Al llegar la medianoche de cada día, el sistema reinicia el contador al número inicial automáticamente.
                                    </div>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={autoResetDaily}
                                    onChange={(e) => setAutoResetDaily(e.target.checked)}
                                    style={{
                                        width: '22px',
                                        height: '22px',
                                        cursor: 'pointer',
                                        accentColor: '#16a34a'
                                    }}
                                />
                            </label>

                            {autoResetDaily && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed #cbd5e1' }}>
                                    <label style={{ fontSize: '13px', color: '#475569', fontWeight: '600' }}>
                                        Número inicial de cada día:
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={initialNumberInput}
                                        onChange={(e) => setInitialNumberInput(e.target.value)}
                                        style={{
                                            width: '70px',
                                            padding: '6px 10px',
                                            fontSize: '14px',
                                            fontWeight: '700',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: '6px',
                                            textAlign: 'center'
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Action buttons */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
                            <button
                                type="button"
                                onClick={handleResetNow}
                                disabled={saving}
                                style={{
                                    padding: '12px 18px',
                                    backgroundColor: '#fef3c7',
                                    color: '#92400e',
                                    border: '1px solid #fde68a',
                                    borderRadius: '10px',
                                    fontWeight: '700',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'background-color 0.2s'
                                }}
                            >
                                <span><i className="bi bi-arrow-repeat"></i></span> Reiniciar a #{initialNumberInput || 1} Ahora
                            </button>

                            <button
                                type="submit"
                                disabled={saving}
                                style={{
                                    padding: '12px 24px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    backgroundColor: '#16a34a',
                                    color: '#ffffff',
                                    fontWeight: '700',
                                    fontSize: '14px',
                                    cursor: saving ? 'not-allowed' : 'pointer',
                                    opacity: saving ? 0.7 : 1,
                                    boxShadow: '0 4px 6px -1px rgba(22, 163, 74, 0.3)'
                                }}
                            >
                                {saving ? 'Guardando...' : 'Guardar Cambios'}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Info Card */}
                <div style={{
                    backgroundColor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '14px',
                    padding: '16px 20px',
                    fontSize: '13px',
                    color: '#1e40af',
                    lineHeight: '1.5'
                }}>
                    <strong style={{ display: 'block', marginBottom: '6px', fontSize: '14px' }}>
                        Formato de Impresión en Comandas y Notas de Entrega:
                    </strong>
                    <p style={{ margin: '0 0 8px 0' }}>
                        Cuando se genera un pedido en el Punto de Venta o módulo de Órdenes:
                    </p>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        <li><strong>Comandas de cocina:</strong> Imprime en grande <code>ORDEN #1</code> en la cabecera.</li>
                        <li><strong>Notas de entrega / Facturas:</strong> Imprime <code>ORDEN #1</code> en la cabecera del cliente.</li>
                        <li><strong>Identificador único:</strong> El código global único (ej: <code>ORD-260915...</code>) se conserva como referencia técnica.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default ConfiguracionSecuencia;
